import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Pool } from '../types/pool';
import { api } from '../services/api';
import { getPoolTypeMeta } from '../utils/poolTypeMeta';

export function HomePage() {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const [featuredPools, setFeaturedPools] = useState<Pool[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedPools();
  }, []);

  const loadFeaturedPools = async () => {
    try {
      const pools = await api.getPools();
      // Show first 3 open pools
      setFeaturedPools(pools.filter(p => p.status === 'OPEN').slice(0, 3));
    } catch (err) {
      console.error('Error loading pools:', err);
    } finally {
      setLoading(false);
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              Welcome to ProPoolPicks
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Compete in fantasy pools for NFL, NHL, NBA, College Football, March Madness, and more!
            </p>
            
            {isAuthenticated ? (
              <Link
                to="/pools"
                className="inline-flex items-center px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              >
                View All Pools
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            ) : (
              <button
                onClick={() => loginWithRedirect()}
                className="inline-flex items-center px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              >
                Get Started
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Featured Pools Section */}
      {featuredPools.length > 0 && (
        <div className="bg-gray-100 py-16">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Featured Pools
              </h2>
              <p className="text-lg text-gray-600">
                Join these popular pools today
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredPools.map(pool => {
                const meta = getPoolTypeMeta(pool.type);
                return (
                  <div key={pool.id} className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`w-10 h-10 rounded-lg ${meta.bg} flex items-center justify-center text-lg`} role="img" aria-label={meta.label}>
                        {meta.emoji}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-500">{meta.label}</span>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        {pool.status}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{pool.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {pool.description || 'Join this exciting fantasy pool!'}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-600">
                        {pool._count?.entries || 0} entries
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {pool.entryFee ? `$${pool.entryFee}` : 'Free'}
                      </span>
                    </div>
                    <Link
                      to={`/pools/${pool.slug || pool.id}`}
                      className="block w-full text-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      View Pool
                    </Link>
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/pools"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                View All Pools
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Features Section */}
      <div className="px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Why ProPoolPicks?
          </h2>
          <p className="text-lg text-gray-600">
            The ultimate fantasy pool platform for sports fans
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Multiple Sports</h3>
            <p className="text-gray-600">
              Football, hockey, basketball, racing, and more. One platform for all your pools.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Easy to Use</h3>
            <p className="text-gray-600">
              Simple interface makes joining and managing your entries a breeze.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Compete with Friends</h3>
            <p className="text-gray-600">
              Create private pools or join public competitions. Track your rank in real-time.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      {!isAuthenticated && (
        <div className="bg-blue-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Join the Competition?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Sign up now and start competing in fantasy pools across multiple sports!
            </p>
            <button
              onClick={() => loginWithRedirect()}
              className="px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              Create Your Account
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
